import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import styled, { css } from 'styled-components';
const hoverStyles = css `
  color: var(--colourTextActive);
  border-color: var(--colourBorderActive);
  background-size: 100% 100%;
  text-decoration-color: var(--colourTextDecorationActive);

  [data-site='ess'] & {
    &[data-type='default'],
    &[data-type='secondary'],
    &[data-type='outline'] {
      &::before,
      &::after {
        background-color: var(--colourBgActive);
        opacity: 1;
      }

      &::before {
        transform: translateX(100%);
      }

      &::after {
        transform: translateX(125%);
      }
    }

    &[data-type='back'] {
      &::before,
      &::after {
        background-color: var(--colourBgActive);
        opacity: 1;
      }

      &::before {
        transform: translateX(-100%);
      }

      &::after {
        transform: translateX(-125%);
      }
    }
  }

  [data-site='mep'] & {
    &[data-type='default'],
    &[data-type='secondary'],
    &[data-type='secondary-alt'],
    &[data-type='outline'],
    &[data-type='back'] {
      &::before {
        opacity: var(--hoverOpacity);
      }
    }
  }
`;
export const CommonButtonStyles = css `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: var(--borderWidth) solid var(--colourBorder);
  background: var(--colourBg);
  background-image: linear-gradient(to right, var(--colourBgActive), var(--colourBgActive));
  background-repeat: no-repeat;
  background-size: 0% 100%;
  color: var(--colourText);
  height: 44px;
  padding: 0 ${fluid(12, 16)};
  text-decoration: none;
  user-select: none;
  width: auto;
`;
const Container = styled.button `
  ${CommonButtonStyles};

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform, opacity', 0.15)};

    span& {
      button:active &,
      a:active & {
        transform: translateY(1.5px);
      }
    }

    &:not(span) {
      &:active {
        transform: translateY(1.5px);
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${transition('background-size, border-color, color, text-decoration-color, opacity', 0.2)};

    span& {
      .focus-ring & {
        outline: auto;
        outline-offset: 3px;
        outline-width: 2px;
        outline-style: solid;
      }

      button:hover &,
      a:hover & {
        ${hoverStyles};
      }
    }

    &:not(span) {
      cursor: pointer;

      &.focus-ring {
        outline: auto;
        outline-offset: 3px;
        outline-width: 2px;
        outline-style: solid;
      }

      &:hover {
        ${hoverStyles};

        background-color: var(--colourBg);
        background-size: 100% 100%;
      }
    }
  }

  /* ESS hover icon styles */
  [data-site='ess'] & {
    &[data-type='default'],
    &[data-type='secondary'],
    &[data-type='outline'] {
      position: relative;
      z-index: 1;

      &::before,
      &::after {
        ${transition('transform, background-color, opacity', 0.3)};

        content: '';
        position: absolute;
        top: 0;
        background-color: var(--colourBg);
        height: 44px;
        width: 30px;
        z-index: -1;
        opacity: 0;
      }

      &:before {
        clip-path: polygon(50% 0, 100% 50%, 50% 100%, 0% 100%, 0 50%, 0 0);
        right: 12px;

        @media ${until(Device.TabletLarge)} {
          transform: translateX(100%);
          opacity: 1;
        }
      }

      &:after {
        clip-path: polygon(30% 0, 80% 50%, 30% 100%, 0% 100%, 50% 50%, 0 0);
        right: calc(var(--borderWidth) * -1);

        @media ${until(Device.TabletLarge)} {
          transform: translateX(125%);
          opacity: 1;
        }
      }
    }

    &[data-type='back'] {
      position: relative;
      z-index: 1;

      &::before,
      &::after {
        ${transition('transform, background-color, opacity', 0.3)};

        content: '';
        position: absolute;
        top: 0;
        background-color: var(--colourBg);
        height: 44px;
        width: 30px;
        z-index: -1;
        opacity: 0;
      }

      &:before {
        clip-path: polygon(50% 0, 0 50%, 50% 100%, 100% 100%, 100% 50%, 100% 0);
        left: 12px;

        @media ${until(Device.TabletLarge)} {
          transform: translateX(-100%);
          opacity: 1;
        }
      }

      &:after {
        clip-path: polygon(70% 0, 20% 50%, 70% 100%, 100% 100%, 50% 50%, 100% 0);
        left: calc(var(--borderWidth) * -1);

        @media ${until(Device.TabletLarge)} {
          transform: translateX(-125%);
          opacity: 1;
        }
      }
    }

    &[data-type='outline'] {
      &::before,
      &::after {
        top: -0.05rem;
      }
    }
  }

  /* MEP hover overlay styles */
  [data-site='mep'] & {
    border-radius: 6px;

    &[data-type='default'],
    &[data-type='secondary'],
    &[data-type='secondary-alt'],
    &[data-type='outline'],
    &[data-type='back'] {
      position: relative;

      &::before {
        ${transition('opacity', 0.25)};

        border-radius: 6px;
        content: '';
        display: block;
        background-color: ${brand.mep.primary};
        position: absolute;
        top: calc(var(--borderWidth) * -1);
        left: calc(var(--borderWidth) * -1);
        height: calc(100% + (var(--borderWidth) * 2));
        width: calc(100% + (var(--borderWidth) * 2));
        clip-path: polygon(
          0% 100%,
          5% 77%,
          9% 66%,
          14% 60%,
          20% 56%,
          28% 52%,
          64% 52%,
          72% 51%,
          80% 47%,
          86% 41%,
          91% 29%,
          95% 17%,
          100% 0,
          100% 100%
        );
        opacity: 0;
      }
    }
  }

  &[data-type='link'],
  &[data-type='link-alt'],
  &[data-type='link-black'] {
    padding: 0;
    text-decoration: underline;
    text-decoration-color: var(--colourTextDecoration);
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
    height: auto;
  }

  &[data-icon-only='true'] {
    height: ${fluid(44, 60)};
    justify-content: center;
    padding: 0;
    width: ${fluid(44, 60)};
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    opacity: 0.66;
    background-size: 0% 100%;
    pointer-events: none;
    text-decoration: none;

    &::before,
    &::after {
      opacity: 0;
    }
  }

  /* Colour settings */

  &[data-type='default'] {
    [data-site='ess'] & {
      --borderWidth: 0px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.global.red};
      --colourBg: ${brand.global.red};
      --colourBgActive: ${brand.global.red};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};

      @media ${from(Device.TabletLarge)} {
        --colourBorder: ${brand.global.white};
        --colourBg: ${brand.global.white};
        --colourText: ${brand.ess.secondary.darkGrey};
      }
    }

    [data-site='mep'] & {
      --borderWidth: 1px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.global.white};
      --colourBg: ${brand.global.red};
      --colourBgActive: ${brand.global.white};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.red};
      --hoverOpacity: 0.15;
    }
  }

  &[data-type='secondary'],
  /* secondary-alt is MEP only, use secondary as fallback on ESS */
  &[data-type='secondary-alt'],
  &[data-type='back'] {
    [data-site='ess'] & {
      --borderWidth: 0px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.ess.secondary.darkGrey};
      --colourBg: ${brand.global.red};
      --colourBgActive: ${brand.ess.secondary.darkGrey};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};
    }
  }

  &[data-type='secondary'] {
    [data-site='mep'] & {
      --borderWidth: 1px;
      --colourBorder: ${brand.global.white};
      --colourBorderActive: ${brand.global.red};
      --colourBg: ${brand.global.white};
      --colourBgActive: ${brand.global.red};
      --colourText: ${brand.global.red};
      --colourTextActive: ${brand.global.white};
      --hoverOpacity: 0.33;
    }
  }

  &[data-type='secondary-alt'] {
    [data-site='mep'] & {
      --borderWidth: 1px;
      --colourBorder: ${brand.global.white};
      --colourBorderActive: ${brand.global.white};
      --colourBg: ${brand.global.white};
      --colourBgActive: ${brand.global.white};
      --colourText: ${brand.global.red};
      --colourTextActive: ${brand.global.red};
      --hoverOpacity: 0.15;
    }
  }

  &[data-type='outline'] {
    [data-site='ess'] & {
      --borderWidth: 0.05rem;
      --colourBorder: ${brand.ess.secondary.darkGrey};
      --colourBorderActive: ${brand.ess.secondary.darkGrey};
      --colourBg: ${brand.ess.secondary.darkGrey};
      --colourBgActive: ${brand.ess.secondary.darkGrey};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};

      @media ${from(Device.TabletLarge)} {
        --colourBorder: ${brand.global.red};
        --colourBg: transparent;
        --colourText: ${brand.global.red};
      }
    }

    [data-site='mep'] & {
      --borderWidth: 2px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.global.red};
      --colourBg: transparent;
      --colourBgActive: ${brand.global.red};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};
      --hoverOpacity: 0.33;
    }
  }

  &[data-type='outline-alt'] {
    [data-site='ess'] & {
      --borderWidth: 1px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.global.red};
      --colourBg: ${brand.global.red};
      --colourBgActive: ${brand.global.red};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};

      @media ${from(Device.TabletLarge)} {
        --colourBorder: ${brand.global.white};
        --colourBg: transparent;
      }
    }

    [data-site='mep'] & {
      --borderWidth: 2px;
      --colourBorder: ${brand.global.red};
      --colourBorderActive: ${brand.global.red};
      --colourBg: ${brand.global.red};
      --colourBgActive: ${brand.global.red};
      --colourText: ${brand.global.white};
      --colourTextActive: ${brand.global.white};

      @media ${from(Device.TabletLarge)} {
        --colourBorder: ${brand.global.white};
        --colourBg: transparent;
      }
    }
  }

  &[data-type='link'] {
    --borderWidth: 0;
    --colourBorder: transparent;
    --colourBorderActive: transparent;
    --colourBg: transparent;
    --colourBgActive: transparent;
    --colourText: ${brand.global.white};
    --colourTextActive: ${brand.global.white};
    --colourTextDecoration: ${brand.global.white};
    --colourTextDecorationActive: ${brand.global.white};

    @media ${from(Device.TabletLarge)} {
      --colourTextDecoration: transparent;
    }
  }

  &[data-type='link-alt'] {
    --borderWidth: 0;
    --colourBorder: transparent;
    --colourBorderActive: transparent;
    --colourBg: transparent;
    --colourBgActive: transparent;
    --colourText: ${brand.global.red};
    --colourTextActive: ${brand.global.red};
    --colourTextDecoration: ${brand.global.red};
    --colourTextDecorationActive: ${brand.global.red};

    @media ${from(Device.TabletLarge)} {
      --colourTextDecoration: transparent;
    }
  }

  &[data-type='link-black'] {
    --borderWidth: 0;
    --colourBorder: transparent;
    --colourBorderActive: transparent;
    --colourBg: transparent;
    --colourBgActive: transparent;
    --colourText: #000000;
    --colourTextActive: #000000;
    --colourTextDecoration: #000000;
    --colourTextDecorationActive: #000000;

    @media ${from(Device.TabletLarge)} {
      --colourTextDecoration: transparent;
    }
  }
`;
const Text = styled.span `
  ${transition('color', 0.2)}
  ${fonts.daxWide.bold};

  color: currentColor;
  font-size: ${fluid(16, 18)};
  line-height: 1em;
  text-decoration: none;
  text-transform: uppercase;
  /* Ensure text sits on top of MEP overlay */
  position: relative;

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color, transform', 0.2)}

  display: block;
  color: currentColor;
  height: 14px;
  margin: 0;
  padding: 0;
  /* Ensure icon sits on top of MEP overlay */
  position: relative;

  > svg,
  > img {
    height: 14px;
    display: block;
  }

  [data-icon-position='left'] & {
    margin-right: auto;
    order: -1;
    padding-right: 12px;
  }

  [data-icon-position='right'] & {
    margin-left: auto;
    order: 1;
    padding-left: 12px;
  }

  [data-icon-only='true'] & {
    color: currentColor;
    height: 22px;
    margin: 0;
    padding: 0;

    > svg,
    > img {
      height: 22px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-type='link']:hover &,
    ${Container}[data-type='link'].focus-ring &,
    ${Container}[data-type='link-alt']:hover &,
    ${Container}[data-type='link-alt'].focus-ring &,
    ${Container}[data-type='link-black']:hover &,
    ${Container}[data-type='link-black'].focus-ring & {
      transform: translateX(15%);
    }
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
